import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { events as analyticsEvents } from "@/lib/analytics";
import {
  Plus, Search, TicketCheck, BookOpen, Send, Bot, User, Headset, Sparkles,
  Clock, CheckCircle2, AlertTriangle, TrendingUp, Loader2, Star, MessageSquare,
  FileText, BarChart3, ThumbsUp, ThumbsDown, Eye, Tag, Zap, StickyNote,
  Wrench, ShieldCheck, Bug, Settings2, HelpCircle, Lightbulb, Users, ArrowRightLeft,
} from "lucide-react";
import { format, differenceInHours, differenceInMinutes } from "date-fns";
import { ptBR } from "date-fns/locale";
import { motion } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const STATUS_MAP: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  open: { label: "Aberto", variant: "destructive" },
  in_progress: { label: "Em andamento", variant: "default" },
  waiting: { label: "Aguardando", variant: "outline" },
  resolved: { label: "Resolvido", variant: "secondary" },
  closed: { label: "Fechado", variant: "outline" },
  reopened: { label: "Reaberto", variant: "destructive" },
};

const PRIORITY_MAP: Record<string, { label: string; color: string }> = {
  low: { label: "Baixa", color: "text-muted-foreground" },
  medium: { label: "Média", color: "text-yellow-500" },
  high: { label: "Alta", color: "text-orange-500" },
  urgent: { label: "Urgente", color: "text-destructive" },
};

const SENDER_ICON: Record<string, React.ReactNode> = {
  customer: <User className="h-4 w-4" />,
  agent: <Headset className="h-4 w-4" />,
  ai: <Bot className="h-4 w-4" />,
};

const SLA_HOURS: Record<string, number> = { urgent: 2, high: 8, medium: 24, low: 72 };
const PIE_COLORS = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))", "hsl(var(--muted))"];

const RESOLUTION_CATEGORIES: Record<string, string> = {
  bug_fix: "Correção de Bug",
  configuration: "Configuração",
  user_error: "Erro do Usuário",
  feature_request: "Solicitação de Recurso",
  known_issue: "Problema Conhecido",
  documentation: "Documentação",
  other: "Outro",
};

// ═══ SLA Badge ═══
function SLABadge({ ticket }: { ticket: any }) {
  if (ticket.status === "resolved" || ticket.status === "closed") return null;
  const deadline = ticket.sla_deadline ? new Date(ticket.sla_deadline) : null;
  if (!deadline) {
    const slaH = SLA_HOURS[ticket.priority] || 24;
    const elapsed = differenceInHours(new Date(), new Date(ticket.created_at));
    const pct = Math.min(100, (elapsed / slaH) * 100);
    const breached = elapsed > slaH;
    return (
      <div className="space-y-0.5">
        <span className={`text-[10px] ${breached ? "text-destructive font-medium" : "text-muted-foreground"}`}>
          SLA: {elapsed}h / {slaH}h {breached && "⚠️"}
        </span>
        <Progress value={pct} className="h-1" />
      </div>
    );
  }
  const remaining = differenceInMinutes(deadline, new Date());
  const breached = remaining <= 0;
  const hoursLeft = Math.abs(Math.floor(remaining / 60));
  const minsLeft = Math.abs(remaining % 60);
  const color = breached ? "text-destructive" : remaining < 120 ? "text-yellow-500" : "text-emerald-500";
  return (
    <span className={`text-[10px] font-medium ${color}`}>
      {breached ? `SLA violado há ${hoursLeft}h${minsLeft}m` : `SLA: ${hoursLeft}h${minsLeft}m restantes`}
    </span>
  );
}

// ═══ CSAT Stars ═══
function CSATStars({ value, onChange }: { value: number; onChange?: (v: number) => void }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`h-4 w-4 cursor-pointer transition-colors ${i <= value ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`}
          onClick={() => onChange?.(i)} />
      ))}
    </div>
  );
}

export default function Support() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState<string | null>(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [newArticleOpen, setNewArticleOpen] = useState(false);
  const [newMacroOpen, setNewMacroOpen] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: "", description: "", priority: "medium", requester_name: "", requester_email: "" });
  const [articleForm, setArticleForm] = useState({ title: "", content: "", category: "", published: false });
  const [macroForm, setMacroForm] = useState({ name: "", content: "", category: "general" });
  const [feedbackForm, setFeedbackForm] = useState({ rating: 0, comment: "" });
  const [feedbackTicketId, setFeedbackTicketId] = useState<string | null>(null);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [resolveForm, setResolveForm] = useState({ resolution_notes: "", resolution_category: "other", root_cause: "" });
  const [troubleshootLoading, setTroubleshootLoading] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [csatAutoShown, setCsatAutoShown] = useState<Set<string>>(new Set());
  // ─── Queries ───
  const { data: orgMembership } = useQuery({
    queryKey: ["my-org-membership", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data } = await supabase.from("org_members").select("org_id").eq("user_id", user!.id).limit(1).maybeSingle();
      return data;
    },
  });
  const orgId = orgMembership?.org_id;

  const { data: tickets = [], isLoading: loadingTickets } = useQuery({
    queryKey: ["support-tickets"],
    queryFn: async () => {
      const { data, error } = await supabase.from("support_tickets").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: articles = [], isLoading: loadingArticles } = useQuery({
    queryKey: ["kb-articles"],
    queryFn: async () => {
      const { data, error } = await supabase.from("kb_articles").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: messages = [] } = useQuery({
    queryKey: ["support-messages", selectedTicket],
    enabled: !!selectedTicket,
    queryFn: async () => {
      const { data, error } = await supabase.from("support_messages").select("*").eq("ticket_id", selectedTicket!).order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const { data: macros = [] } = useQuery({
    queryKey: ["support-macros", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase.from("support_macros").select("*").eq("org_id", orgId!).order("usage_count", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: feedbacks = [] } = useQuery({
    queryKey: ["ticket-feedback", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase.from("ticket_feedback").select("*, support_tickets(subject)").order("created_at", { ascending: false }).limit(100);
      if (error) throw error;
      return data;
    },
  });

  // Human assignment queue
  const { data: queueItems = [] } = useQuery({
    queryKey: ["support-queue", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("support_assignment_queue")
        .select("*, support_tickets(id, subject, priority, status, requester_name, created_at, sla_deadline, ai_category, ai_confidence, triage_confidence)")
        .eq("org_id", orgId!)
        .is("claimed_by", null)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  // Org members for transfer
  const { data: orgAgents = [] } = useQuery({
    queryKey: ["org-agents", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("org_members")
        .select("user_id, role")
        .eq("org_id", orgId!)
        .in("role", ["owner", "admin"]);
      if (error) throw error;
      // Get profiles for names
      if (data && data.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", data.map(d => d.user_id));
        return data.map(d => ({
          ...d,
          profile: profiles?.find(p => p.id === d.user_id),
        }));
      }
      return data;
    },
  });

  // ─── Metrics ───
  const ticketMetrics = (() => {
    const open = tickets.filter((t: any) => t.status === "open" || t.status === "reopened").length;
    const inProgress = tickets.filter((t: any) => t.status === "in_progress").length;
    const resolved = tickets.filter((t: any) => t.status === "resolved" || t.status === "closed").length;
    const breached = tickets.filter((t: any) => {
      if (t.status === "resolved" || t.status === "closed") return false;
      if (t.sla_deadline) return new Date() > new Date(t.sla_deadline);
      const slaH = SLA_HOURS[t.priority] || 24;
      return differenceInHours(new Date(), new Date(t.created_at)) > slaH;
    }).length;
    const avgResolutionH = (() => {
      const rt = tickets.filter((t: any) => t.resolved_at);
      if (!rt.length) return 0;
      return Math.round(rt.reduce((s: number, t: any) => s + differenceInHours(new Date(t.resolved_at!), new Date(t.created_at)), 0) / rt.length);
    })();
    const aiResolved = tickets.filter((t: any) => t.ai_category && (t.status === "resolved" || t.status === "closed")).length;
    const aiRate = tickets.length ? Math.round((aiResolved / Math.max(resolved, 1)) * 100) : 0;
    const avgCsat = feedbacks.length ? (feedbacks.reduce((s: number, f: any) => s + f.rating, 0) / feedbacks.length).toFixed(1) : "-";
    return { open, inProgress, resolved, breached, avgResolutionH, aiRate, avgCsat, total: tickets.length };
  })();

  // Charts data
  const categoryData = (() => {
    const cats: Record<string, number> = {};
    tickets.forEach((t: any) => { cats[t.ai_category || t.category || "general"] = (cats[t.ai_category || t.category || "general"] || 0) + 1; });
    return Object.entries(cats).map(([name, value]) => ({ name, value }));
  })();

  const statusData = (() => {
    const st: Record<string, number> = {};
    tickets.forEach((t: any) => { st[STATUS_MAP[t.status]?.label || t.status] = (st[STATUS_MAP[t.status]?.label || t.status] || 0) + 1; });
    return Object.entries(st).map(([name, value]) => ({ name, value }));
  })();

  // ─── Mutations ───
  const createTicket = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("Org não encontrada");
      const { data, error } = await supabase.from("support_tickets").insert({
        org_id: orgId, ...ticketForm, user_id: user?.id || null, channel: "dashboard",
        sla_deadline: new Date(Date.now() + (SLA_HOURS[ticketForm.priority] || 24) * 3600000).toISOString(),
      }).select().single();
      if (error) throw error;
      try {
        await supabase.functions.invoke("support-triage", {
          body: { ticket_id: data.id, subject: ticketForm.subject, description: ticketForm.description, org_id: orgId },
        });
      } catch {}
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      setNewTicketOpen(false);
      setTicketForm({ subject: "", description: "", priority: "medium", requester_name: "", requester_email: "" });
      toast.success("Ticket criado com sucesso");
      analyticsEvents.ticketOpened(data?.id);
    },
    onError: () => toast.error("Erro ao criar ticket"),
  });

  const updateTicketStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const updates: any = { status };
      if (status === "resolved") updates.resolved_at = new Date().toISOString();
      const { error } = await supabase.from("support_tickets").update(updates).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["support-tickets"] }); toast.success("Status atualizado"); },
  });

  const resolveTicket = useMutation({
    mutationFn: async () => {
      if (!selectedTicket) return;
      const { error } = await supabase.from("support_tickets").update({
        status: "resolved",
        resolved_at: new Date().toISOString(),
        resolved_by: user?.id || null,
        resolution_notes: resolveForm.resolution_notes,
        resolution_category: resolveForm.resolution_category,
        root_cause: resolveForm.root_cause,
      }).eq("id", selectedTicket);
      if (error) throw error;
      // Add resolution message to thread
      await supabase.from("support_messages").insert({
        ticket_id: selectedTicket,
        sender_type: "agent",
        sender_name: user?.name || "Agente",
        sender_id: user?.id || null,
        is_internal_note: false,
        content: `✅ **Ticket resolvido**\n\n**Categoria:** ${RESOLUTION_CATEGORIES[resolveForm.resolution_category] || resolveForm.resolution_category}\n**Causa raiz:** ${resolveForm.root_cause || "N/A"}\n**Notas:** ${resolveForm.resolution_notes || "N/A"}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      queryClient.invalidateQueries({ queryKey: ["support-messages", selectedTicket] });
      setResolveDialogOpen(false);
      setResolveForm({ resolution_notes: "", resolution_category: "other", root_cause: "" });
      toast.success("Ticket resolvido com sucesso");
    },
    onError: () => toast.error("Erro ao resolver ticket"),
  });

  const handleStatusChange = (id: string, status: string) => {
    if (status === "resolved") {
      setResolveDialogOpen(true);
    } else {
      updateTicketStatus.mutate({ id, status });
    }
  };

  const troubleshootTicket = async () => {
    if (!selectedTicket || !currentTicket) return;
    setTroubleshootLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("support-triage", {
        body: {
          ticket_id: selectedTicket,
          subject: currentTicket.subject,
          description: currentTicket.description,
          org_id: orgId,
          mode: "troubleshoot",
        },
      });
      queryClient.invalidateQueries({ queryKey: ["support-messages", selectedTicket] });
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      toast.success("Diagnóstico gerado com sucesso");
    } catch (e) {
      toast.error("Erro ao gerar diagnóstico");
    } finally {
      setTroubleshootLoading(false);
    }
  };

  const sendReply = useMutation({
    mutationFn: async () => {
      if (!selectedTicket || !replyContent.trim()) return;
      const { error } = await supabase.from("support_messages").insert({
        ticket_id: selectedTicket, sender_type: isInternalNote ? "agent" : "agent",
        sender_name: user?.name || "Agente", content: replyContent.trim(),
        is_internal_note: isInternalNote, sender_id: user?.id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-messages", selectedTicket] });
      setReplyContent("");
      setIsInternalNote(false);
      toast.success(isInternalNote ? "Nota interna adicionada" : "Resposta enviada");
    },
  });

  const aiReply = useMutation({
    mutationFn: async () => {
      if (!selectedTicket) return;
      const ticket = tickets.find((t: any) => t.id === selectedTicket);
      if (!ticket) return;
      await supabase.functions.invoke("support-triage", {
        body: { ticket_id: selectedTicket, subject: ticket.subject, description: ticket.description, org_id: orgId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-messages", selectedTicket] });
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      toast.success("Resposta IA gerada");
    },
  });

  const createArticle = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("Org não encontrada");
      const slug = articleForm.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      const { error } = await supabase.from("kb_articles").insert({
        org_id: orgId, ...articleForm, slug, status: articleForm.published ? "published" : "draft",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kb-articles"] });
      setNewArticleOpen(false);
      setArticleForm({ title: "", content: "", category: "", published: false });
      toast.success("Artigo criado");
    },
    onError: () => toast.error("Erro ao criar artigo"),
  });

  const toggleArticleStatus = useMutation({
    mutationFn: async ({ id, currentStatus }: { id: string; currentStatus: string }) => {
      const newStatus = currentStatus === "published" ? "draft" : "published";
      const { error } = await supabase.from("kb_articles").update({ status: newStatus, published: newStatus === "published" }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["kb-articles"] }); toast.success("Artigo atualizado"); },
  });

  const createMacro = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("Org não encontrada");
      const vars = (macroForm.content.match(/\{(\w+)\}/g) || []).map(v => v.replace(/[{}]/g, ""));
      const { error } = await supabase.from("support_macros").insert({
        org_id: orgId, name: macroForm.name, content: macroForm.content, category: macroForm.category, variables: vars,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-macros"] });
      setNewMacroOpen(false);
      setMacroForm({ name: "", content: "", category: "general" });
      toast.success("Macro criada");
    },
    onError: () => toast.error("Erro ao criar macro"),
  });

  const submitFeedback = useMutation({
    mutationFn: async () => {
      if (!feedbackTicketId || !orgId || !feedbackForm.rating) return;
      await supabase.functions.invoke("feedback-collect", {
        body: { ticket_id: feedbackTicketId, org_id: orgId, user_id: user?.id, ...feedbackForm },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ticket-feedback"] });
      setFeedbackTicketId(null);
      setFeedbackForm({ rating: 0, comment: "" });
      toast.success("Feedback enviado");
    },
  });

  const applyMacro = (macro: any) => {
    let content = macro.content;
    content = content.replace(/\{user_name\}/g, user?.name || "");
    content = content.replace(/\{org_name\}/g, "");
    setReplyContent(content);
    supabase.from("support_macros").update({ usage_count: (macro.usage_count || 0) + 1 }).eq("id", macro.id).then(() => {
      queryClient.invalidateQueries({ queryKey: ["support-macros"] });
    });
  };

  // Claim ticket from queue
  const claimTicket = useMutation({
    mutationFn: async (ticketId: string) => {
      if (!user?.id) return;
      const { error } = await supabase.from("support_assignment_queue")
        .update({ claimed_by: user.id, claimed_at: new Date().toISOString() })
        .eq("ticket_id", ticketId);
      if (error) throw error;
      await supabase.from("support_tickets").update({
        assigned_to: user.id,
        status: "in_progress",
      }).eq("id", ticketId);
      await supabase.from("support_messages").insert({
        ticket_id: ticketId,
        sender_type: "agent",
        sender_name: user?.name || "Agente",
        sender_id: user.id,
        content: `👤 Ticket assumido por ${user?.name || "agente"}.`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-queue"] });
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      toast.success("Ticket assumido com sucesso");
    },
    onError: () => toast.error("Erro ao assumir ticket"),
  });

  // Transfer ticket to another agent
  const transferTicket = useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!selectedTicket) return;
      const { error } = await supabase.from("support_tickets").update({
        assigned_to: targetUserId,
      }).eq("id", selectedTicket);
      if (error) throw error;
      await supabase.from("support_assignment_queue").upsert(
        { org_id: orgId!, ticket_id: selectedTicket, claimed_by: targetUserId, claimed_at: new Date().toISOString() },
        { onConflict: "ticket_id" }
      );
      const target: any = orgAgents.find((a: any) => a.user_id === targetUserId);
      await supabase.from("support_messages").insert({
        ticket_id: selectedTicket,
        sender_type: "agent",
        sender_name: user?.name || "Agente",
        sender_id: user?.id || null,
        content: `🔄 Ticket transferido para ${target?.profile?.name || "outro agente"}.`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
      queryClient.invalidateQueries({ queryKey: ["support-queue"] });
      setTransferDialogOpen(false);
      toast.success("Ticket transferido");
    },
    onError: () => toast.error("Erro ao transferir"),
  });

  const filteredTickets = tickets.filter((t: any) => {
    const matchSearch = !search || t.subject.toLowerCase().includes(search.toLowerCase()) || (t.requester_name || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const currentTicket = tickets.find((t: any) => t.id === selectedTicket);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-headline">Suporte</h1>
        <p className="text-muted-foreground text-sm">Tickets, IA, Base de Conhecimento e Métricas</p>
      </div>

      {/* Metrics Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: "Abertos", value: ticketMetrics.open, icon: AlertTriangle, color: "text-destructive" },
          { label: "Em Andamento", value: ticketMetrics.inProgress, icon: Clock, color: "text-primary" },
          { label: "Resolvidos", value: ticketMetrics.resolved, icon: CheckCircle2, color: "text-emerald-500" },
          { label: "SLA Violado", value: ticketMetrics.breached, icon: AlertTriangle, color: "text-destructive" },
          { label: "Resolução Média", value: `${ticketMetrics.avgResolutionH}h`, icon: TrendingUp, color: "text-primary" },
          { label: "IA Resolution", value: `${ticketMetrics.aiRate}%`, icon: Bot, color: "text-primary" },
          { label: "CSAT Médio", value: ticketMetrics.avgCsat, icon: Star, color: "text-yellow-500" },
        ].map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Card className="border-border/50">
              <CardContent className="pt-3 pb-3 flex items-center gap-2">
                <m.icon className={`w-4 h-4 ${m.color} shrink-0`} />
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground truncate">{m.label}</p>
                  <p className="text-lg font-bold font-headline">{m.value}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <Tabs defaultValue="inbox" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="inbox" className="gap-1.5"><TicketCheck className="h-4 w-4" /> Inbox</TabsTrigger>
          <TabsTrigger value="queue" className="gap-1.5"><Users className="h-4 w-4" /> Fila Humana {queueItems.length > 0 && <Badge variant="destructive" className="ml-1 text-[10px] h-4 px-1">{queueItems.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="kb" className="gap-1.5"><BookOpen className="h-4 w-4" /> Base de Conhecimento</TabsTrigger>
          <TabsTrigger value="macros" className="gap-1.5"><Zap className="h-4 w-4" /> Macros</TabsTrigger>
          <TabsTrigger value="metrics" className="gap-1.5"><BarChart3 className="h-4 w-4" /> Métricas</TabsTrigger>
          <TabsTrigger value="feedback" className="gap-1.5"><Star className="h-4 w-4" /> Feedback</TabsTrigger>
        </TabsList>

        {/* ═══ INBOX TAB ═══ */}
        <TabsContent value="inbox" className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar tickets..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="open">Abertos</SelectItem>
                <SelectItem value="in_progress">Em andamento</SelectItem>
                <SelectItem value="waiting">Aguardando</SelectItem>
                <SelectItem value="resolved">Resolvidos</SelectItem>
                <SelectItem value="closed">Fechados</SelectItem>
                <SelectItem value="reopened">Reabertos</SelectItem>
              </SelectContent>
            </Select>
            <Dialog open={newTicketOpen} onOpenChange={setNewTicketOpen}>
              <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" /> Novo Ticket</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Novo Ticket</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Input placeholder="Nome do solicitante" value={ticketForm.requester_name} onChange={(e) => setTicketForm(p => ({ ...p, requester_name: e.target.value }))} />
                  <Input placeholder="Email do solicitante" type="email" value={ticketForm.requester_email} onChange={(e) => setTicketForm(p => ({ ...p, requester_email: e.target.value }))} />
                  <Input placeholder="Assunto" value={ticketForm.subject} onChange={(e) => setTicketForm(p => ({ ...p, subject: e.target.value }))} />
                  <Textarea placeholder="Descrição do problema" rows={4} value={ticketForm.description} onChange={(e) => setTicketForm(p => ({ ...p, description: e.target.value }))} />
                  <Select value={ticketForm.priority} onValueChange={(v) => setTicketForm(p => ({ ...p, priority: v }))}>
                    <SelectTrigger><SelectValue placeholder="Prioridade" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baixa</SelectItem>
                      <SelectItem value="medium">Média</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="urgent">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={() => createTicket.mutate()} disabled={!ticketForm.subject || createTicket.isPending} className="w-full">
                    {createTicket.isPending ? "Criando..." : "Criar Ticket"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Ticket List */}
            <div className="lg:col-span-1 space-y-2">
              {loadingTickets ? (
                <p className="text-sm text-muted-foreground p-4">Carregando...</p>
              ) : filteredTickets.length === 0 ? (
                <Card><CardContent className="p-6 text-center text-muted-foreground text-sm">Nenhum ticket encontrado</CardContent></Card>
              ) : (
                <ScrollArea className="h-[600px]">
                  <div className="space-y-2 pr-2">
                    {filteredTickets.map((ticket: any) => (
                      <Card key={ticket.id}
                        className={`cursor-pointer transition-colors hover:border-primary/50 ${selectedTicket === ticket.id ? "border-primary bg-accent/30" : ""}`}
                        onClick={() => setSelectedTicket(ticket.id)}
                      >
                        <CardContent className="p-3 space-y-1.5">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm line-clamp-1">{ticket.subject}</p>
                            <Badge variant={STATUS_MAP[ticket.status]?.variant || "outline"} className="text-[10px] shrink-0">
                              {STATUS_MAP[ticket.status]?.label || ticket.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className={PRIORITY_MAP[ticket.priority]?.color}>{PRIORITY_MAP[ticket.priority]?.label}</span>
                            <span>·</span>
                            <span>{ticket.requester_name || "-"}</span>
                            <span>·</span>
                            <span>{format(new Date(ticket.created_at), "dd/MM HH:mm")}</span>
                          </div>
                          {ticket.ai_category && (
                            <div className="flex items-center gap-1 text-xs text-primary">
                              <Sparkles className="h-3 w-3" />
                              <span>{ticket.ai_category}</span>
                              {ticket.ai_confidence && <span className="text-muted-foreground">({Math.round(Number(ticket.ai_confidence) * 100)}%)</span>}
                            </div>
                          )}
                          {ticket.channel && ticket.channel !== "dashboard" && (
                            <Badge variant="outline" className="text-[9px]">{ticket.channel}</Badge>
                          )}
                          <SLABadge ticket={ticket} />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            {/* Ticket Detail */}
            <div className="lg:col-span-2">
              {currentTicket ? (
                <Card className="h-[600px] flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <CardTitle className="text-base">{currentTicket.subject}</CardTitle>
                        <CardDescription>{currentTicket.requester_name} · {currentTicket.requester_email}</CardDescription>
                        <div className="flex items-center gap-2 mt-1">
                          <SLABadge ticket={currentTicket} />
                          {currentTicket.channel && <Badge variant="outline" className="text-[9px]">{currentTicket.channel}</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select value={currentTicket.status} onValueChange={(v) => handleStatusChange(currentTicket.id, v)}>
                          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button variant="outline" size="sm" onClick={troubleshootTicket} disabled={troubleshootLoading || currentTicket.status === "resolved" || currentTicket.status === "closed"}>
                          {troubleshootLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Wrench className="h-3 w-3 mr-1" />}
                          Diagnosticar
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setTransferDialogOpen(true)} disabled={currentTicket.status === "resolved" || currentTicket.status === "closed"}>
                          <ArrowRightLeft className="h-3 w-3 mr-1" /> Transferir
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => { setFeedbackTicketId(currentTicket.id); setFeedbackForm({ rating: 0, comment: "" }); }}>
                          <Star className="h-3 w-3 mr-1" /> CSAT
                        </Button>
                      </div>
                    </div>
                    {currentTicket.description && <p className="text-sm text-muted-foreground mt-2">{currentTicket.description}</p>}
                    {/* Resolution info */}
                    {(currentTicket.status === "resolved" || currentTicket.status === "closed") && currentTicket.resolution_notes && (
                      <div className="mt-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400 mb-1">
                          <ShieldCheck className="h-3.5 w-3.5" /> Resolução
                        </div>
                        {currentTicket.resolution_category && (
                          <Badge variant="outline" className="text-[10px] mb-1">{RESOLUTION_CATEGORIES[currentTicket.resolution_category] || currentTicket.resolution_category}</Badge>
                        )}
                        {currentTicket.root_cause && (
                          <p className="text-xs text-muted-foreground"><span className="font-medium">Causa raiz:</span> {currentTicket.root_cause}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">{currentTicket.resolution_notes}</p>
                      </div>
                    )}
                  </CardHeader>
                  <Separator />
                  <ScrollArea className="flex-1 px-4">
                    <div className="space-y-3 py-4">
                      {messages.map((msg: any) => (
                        <div key={msg.id} className={`flex gap-2 ${msg.sender_type === "agent" && !msg.is_internal_note ? "justify-end" : ""}`}>
                          <div className={`max-w-[80%] rounded-lg p-3 text-sm ${
                            msg.is_internal_note ? "bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700" :
                            msg.sender_type === "agent" ? "bg-primary text-primary-foreground" :
                            msg.sender_type === "ai" ? "bg-accent border border-primary/20" : "bg-muted"
                          }`}>
                            <div className="flex items-center gap-1.5 mb-1 text-xs opacity-70">
                              {msg.is_internal_note ? <StickyNote className="h-3 w-3" /> : SENDER_ICON[msg.sender_type]}
                              <span>{msg.is_internal_note ? "Nota Interna" : msg.sender_name}</span>
                              <span>· {format(new Date(msg.created_at), "dd/MM HH:mm")}</span>
                            </div>
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                          </div>
                        </div>
                      ))}
                      {messages.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Nenhuma mensagem ainda</p>}
                    </div>
                  </ScrollArea>
                  <Separator />
                  <div className="p-3 space-y-2">
                    {/* Internal note toggle */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Switch id="internal-note" checked={isInternalNote} onCheckedChange={setIsInternalNote} />
                        <Label htmlFor="internal-note" className="text-xs text-muted-foreground">
                          {isInternalNote ? "📝 Nota Interna" : "💬 Resposta"}
                        </Label>
                      </div>
                      {/* Macro selector */}
                      {macros.length > 0 && (
                        <Select onValueChange={(v) => { const m = macros.find((x: any) => x.id === v); if (m) applyMacro(m); }}>
                          <SelectTrigger className="w-[140px] h-7 text-xs"><Zap className="h-3 w-3 mr-1" /> Macros</SelectTrigger>
                          <SelectContent>
                            {macros.map((m: any) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Textarea placeholder={isInternalNote ? "Escrever nota interna..." : "Escrever resposta..."}
                        value={replyContent} onChange={(e) => setReplyContent(e.target.value)} rows={2}
                        className={`resize-none ${isInternalNote ? "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-700" : ""}`}
                      />
                      <div className="flex flex-col gap-1">
                        <Button size="icon" onClick={() => sendReply.mutate()} disabled={!replyContent.trim() || sendReply.isPending}>
                          {isInternalNote ? <StickyNote className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                        </Button>
                        <Button size="icon" variant="outline" onClick={() => aiReply.mutate()} disabled={aiReply.isPending} title="Resposta IA">
                          {aiReply.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ) : (
                <Card className="h-[600px] flex items-center justify-center">
                  <p className="text-muted-foreground text-sm">Selecione um ticket para ver detalhes</p>
                </Card>
              )}
            </div>
          </div>

          {/* CSAT Dialog */}
          <Dialog open={!!feedbackTicketId} onOpenChange={(o) => { if (!o) setFeedbackTicketId(null); }}>
            <DialogContent>
              <DialogHeader><DialogTitle>Avaliar Atendimento</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="flex justify-center"><CSATStars value={feedbackForm.rating} onChange={(v) => setFeedbackForm(p => ({ ...p, rating: v }))} /></div>
                <Textarea placeholder="Comentário (opcional)" value={feedbackForm.comment} onChange={(e) => setFeedbackForm(p => ({ ...p, comment: e.target.value }))} rows={3} />
                <Button onClick={() => submitFeedback.mutate()} disabled={!feedbackForm.rating || submitFeedback.isPending} className="w-full">
                  {submitFeedback.isPending ? "Enviando..." : "Enviar Feedback"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Resolve Dialog */}
          <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-500" /> Resolver Ticket</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Categoria da Resolução</Label>
                  <Select value={resolveForm.resolution_category} onValueChange={(v) => setResolveForm(p => ({ ...p, resolution_category: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(RESOLUTION_CATEGORIES).map(([k, v]) => (
                        <SelectItem key={k} value={k}>
                          <span className="flex items-center gap-2">
                            {k === "bug_fix" && <Bug className="h-3 w-3" />}
                            {k === "configuration" && <Settings2 className="h-3 w-3" />}
                            {k === "user_error" && <HelpCircle className="h-3 w-3" />}
                            {k === "feature_request" && <Lightbulb className="h-3 w-3" />}
                            {k === "known_issue" && <AlertTriangle className="h-3 w-3" />}
                            {k === "documentation" && <FileText className="h-3 w-3" />}
                            {k === "other" && <Tag className="h-3 w-3" />}
                            {v}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Causa Raiz</Label>
                  <Input placeholder="O que causou o problema?" value={resolveForm.root_cause} onChange={(e) => setResolveForm(p => ({ ...p, root_cause: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Notas de Resolução</Label>
                  <Textarea placeholder="Descreva o que foi feito para resolver..." rows={4} value={resolveForm.resolution_notes} onChange={(e) => setResolveForm(p => ({ ...p, resolution_notes: e.target.value }))} />
                </div>
                <Button onClick={() => resolveTicket.mutate()} disabled={!resolveForm.resolution_notes.trim() || resolveTicket.isPending} className="w-full">
                  {resolveTicket.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resolvendo...</> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Resolver Ticket</>}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ═══ KB TAB ═══ */}
        <TabsContent value="kb" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{articles.length} artigo(s)</p>
            <Dialog open={newArticleOpen} onOpenChange={setNewArticleOpen}>
              <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" /> Novo Artigo</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Novo Artigo</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Input placeholder="Título" value={articleForm.title} onChange={(e) => setArticleForm(p => ({ ...p, title: e.target.value }))} />
                  <Input placeholder="Categoria" value={articleForm.category} onChange={(e) => setArticleForm(p => ({ ...p, category: e.target.value }))} />
                  <Textarea placeholder="Conteúdo do artigo..." rows={8} value={articleForm.content} onChange={(e) => setArticleForm(p => ({ ...p, content: e.target.value }))} />
                  <div className="flex items-center gap-2">
                    <Switch id="publish" checked={articleForm.published} onCheckedChange={(c) => setArticleForm(p => ({ ...p, published: c }))} />
                    <Label htmlFor="publish" className="text-sm">Publicar imediatamente</Label>
                  </div>
                  <Button onClick={() => createArticle.mutate()} disabled={!articleForm.title || createArticle.isPending} className="w-full">
                    {createArticle.isPending ? "Criando..." : "Criar Artigo"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {loadingArticles ? (
            <p className="text-sm text-muted-foreground">Carregando...</p>
          ) : articles.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Nenhum artigo na base de conhecimento</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((article: any) => (
                <Card key={article.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-sm">{article.title}</CardTitle>
                      <Badge variant={article.status === "published" || article.published ? "default" : "outline"} className="text-[10px] shrink-0">
                        {article.status === "published" || article.published ? "Publicado" : article.status === "archived" ? "Arquivado" : "Rascunho"}
                      </Badge>
                    </div>
                    {article.category && <CardDescription>{article.category}</CardDescription>}
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-xs text-muted-foreground line-clamp-3">{article.content}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {article.views || 0}</span>
                      <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3" /> {article.helpful_count || 0}</span>
                      <span className="flex items-center gap-1"><ThumbsDown className="h-3 w-3" /> {article.not_helpful_count || 0}</span>
                    </div>
                    <Button variant="outline" size="sm" className="w-full text-xs"
                      onClick={() => toggleArticleStatus.mutate({ id: article.id, currentStatus: article.status || (article.published ? "published" : "draft") })}>
                      {article.status === "published" || article.published ? "Despublicar" : "Publicar"}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ═══ MACROS TAB ═══ */}
        <TabsContent value="macros" className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">{macros.length} macro(s)</p>
            <Dialog open={newMacroOpen} onOpenChange={setNewMacroOpen}>
              <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" /> Nova Macro</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nova Macro</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Input placeholder="Nome da macro" value={macroForm.name} onChange={(e) => setMacroForm(p => ({ ...p, name: e.target.value }))} />
                  <Select value={macroForm.category} onValueChange={(v) => setMacroForm(p => ({ ...p, category: v }))}>
                    <SelectTrigger><SelectValue placeholder="Categoria" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">Geral</SelectItem>
                      <SelectItem value="greeting">Saudação</SelectItem>
                      <SelectItem value="closing">Encerramento</SelectItem>
                      <SelectItem value="technical">Técnico</SelectItem>
                      <SelectItem value="billing">Billing</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea placeholder="Conteúdo da macro. Use {user_name}, {org_name} como variáveis." rows={6}
                    value={macroForm.content} onChange={(e) => setMacroForm(p => ({ ...p, content: e.target.value }))} />
                  <p className="text-xs text-muted-foreground">Variáveis detectadas: {(macroForm.content.match(/\{(\w+)\}/g) || []).join(", ") || "nenhuma"}</p>
                  <Button onClick={() => createMacro.mutate()} disabled={!macroForm.name || !macroForm.content || createMacro.isPending} className="w-full">
                    {createMacro.isPending ? "Criando..." : "Criar Macro"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {macros.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Nenhuma macro configurada</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {macros.map((macro: any) => (
                <Card key={macro.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-sm">{macro.name}</CardTitle>
                      <Badge variant="outline" className="text-[10px]">{macro.category}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-xs text-muted-foreground line-clamp-3 font-mono bg-muted p-2 rounded">{macro.content}</p>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Variáveis: {macro.variables?.length || 0}</span>
                      <span>Usos: {macro.usage_count || 0}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ═══ METRICS TAB ═══ */}
        <TabsContent value="metrics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Tickets por Categoria</CardTitle></CardHeader>
              <CardContent>
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={categoryData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name} (${value})`}>
                        {categoryData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-muted-foreground text-center py-8">Sem dados</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Tickets por Status</CardTitle></CardHeader>
              <CardContent>
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={statusData}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-muted-foreground text-center py-8">Sem dados</p>}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Resumo de KPIs</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center"><p className="text-2xl font-bold font-headline text-primary">{ticketMetrics.aiRate}%</p><p className="text-xs text-muted-foreground">AI Resolution Rate</p></div>
                <div className="text-center"><p className="text-2xl font-bold font-headline">{ticketMetrics.avgResolutionH}h</p><p className="text-xs text-muted-foreground">Tempo Médio Resolução</p></div>
                <div className="text-center"><p className="text-2xl font-bold font-headline text-yellow-500">{ticketMetrics.avgCsat}</p><p className="text-xs text-muted-foreground">CSAT Médio</p></div>
                <div className="text-center"><p className="text-2xl font-bold font-headline text-destructive">{ticketMetrics.breached}</p><p className="text-xs text-muted-foreground">SLA Violados</p></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ FEEDBACK TAB ═══ */}
        <TabsContent value="feedback" className="space-y-4">
          {feedbacks.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">Nenhum feedback recebido</CardContent></Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ticket</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Comentário</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feedbacks.map((fb: any) => (
                      <TableRow key={fb.id} className={fb.rating < 3 ? "bg-destructive/5" : ""}>
                        <TableCell className="text-sm">{(fb as any).support_tickets?.subject || fb.ticket_id.slice(0, 8)}</TableCell>
                        <TableCell><CSATStars value={fb.rating} /></TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[300px] truncate">{fb.comment || "-"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{format(new Date(fb.created_at), "dd/MM/yyyy HH:mm")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══ HUMAN QUEUE TAB ═══ */}
        <TabsContent value="queue" className="space-y-4">
          {queueItems.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhum ticket na fila de revisão humana</p>
                <p className="text-xs mt-1">Tickets com baixa confiança da IA aparecerão aqui</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {queueItems.map((item: any) => {
                const ticket = item.support_tickets;
                if (!ticket) return null;
                return (
                  <motion.div key={item.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                    <Card className="border-yellow-500/30 bg-yellow-50/5">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <CardTitle className="text-sm line-clamp-2">{ticket.subject}</CardTitle>
                          <Badge variant={ticket.priority === "urgent" ? "destructive" : "outline"} className="text-[10px] shrink-0">
                            {PRIORITY_MAP[ticket.priority]?.label || ticket.priority}
                          </Badge>
                        </div>
                        <CardDescription className="text-xs">
                          {ticket.requester_name || "-"} · {format(new Date(ticket.created_at), "dd/MM HH:mm")}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="flex items-center gap-2 text-xs">
                          {ticket.ai_category && (
                            <span className="flex items-center gap-1 text-primary">
                              <Sparkles className="h-3 w-3" /> {ticket.ai_category}
                            </span>
                          )}
                          {ticket.triage_confidence != null && (
                            <span className="text-muted-foreground">
                              Confiança: {Math.round(Number(ticket.triage_confidence) * 100)}%
                            </span>
                          )}
                        </div>
                        <SLABadge ticket={ticket} />
                        <Button size="sm" className="w-full" onClick={() => claimTicket.mutate(ticket.id)} disabled={claimTicket.isPending}>
                          {claimTicket.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Headset className="h-3 w-3 mr-1" />}
                          Assumir Ticket
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5" /> Transferir Ticket
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Selecione o agente para transferir:</p>
            {orgAgents.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nenhum agente disponível</p>
            ) : (
              <div className="space-y-2">
                {orgAgents.map((agent: any) => (
                  <Button
                    key={agent.user_id}
                    variant="outline"
                    className="w-full justify-start gap-2"
                    disabled={agent.user_id === user?.id || transferTicket.isPending}
                    onClick={() => transferTicket.mutate(agent.user_id)}
                  >
                    <User className="h-4 w-4" />
                    <span>{agent.profile?.name || agent.profile?.email || agent.user_id.slice(0, 8)}</span>
                    <Badge variant="outline" className="ml-auto text-[10px]">{agent.role}</Badge>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
